import { Controller } from '@hotwired/stimulus'
import { fetchTurbo } from '../utils/fetch-turbo'

// Ввод счёта матча конструктора турниров. Два режима (см. tournament.scoring_mode):
// 'fixed' — счета сторон в сумме дают match_points, ввод одной стороны
// автоматически дополняет вторую; 'free' — стороны независимы, обе вводятся
// вручную, без авто-дополнения и без потолка. В обоих режимах невалидное
// значение подсвечивается и сбрасывает обе стороны. Сохранение — PATCH с
// дебаунсом, ответ (turbo stream) обновляет таблицу и блоки прогресса, не
// трогая сами инпуты (фокус не теряется).
export default class TournamentScoreController extends Controller {
  static targets = ['a', 'b']
  static values = { url: String, max: Number, free: Boolean }

  change(event) {
    const src = event.target
    const other = src === this.aTarget ? this.bTarget : this.aTarget
    const raw = src.value.trim()
    const value = raw === '' ? null : parseInt(raw, 10)

    const invalid =
      value !== null &&
      (Number.isNaN(value) ||
        value < 0 ||
        (!this.freeValue && value > this.maxValue))

    if (this.freeValue) {
      this.changeFree(src, other, value, invalid)
      return
    }

    if (invalid) {
      src.classList.add('is-invalid')
      other.value = ''
      this.markWinners(null, null)
      this.push(null, null)
      return
    }

    src.classList.remove('is-invalid')
    other.classList.remove('is-invalid')

    if (value === null) {
      other.value = ''
      this.markWinners(null, null)
      this.push(null, null)
      return
    }

    other.value = this.maxValue - value
    this.pushBothScores()
  }

  // Стороны независимы, поэтому правка/очистка одной не должна трогать
  // уже сохранённое значение другой — раньше `value === null` (в т.ч.
  // мимолётный пустой инпут между выделением старой цифры и вводом новой)
  // безусловно стирал other.value и ставил в очередь push(null, null); если
  // пользователь тут же дописывал новую цифру, код уходил в return, не
  // отменяя тот таймер — через 500мс он всё равно стирал реальный счёт
  // на сервере. Теперь push(null, null) уходит только когда обе стороны
  // пустые одновременно — то есть счёт действительно осознанно стёрли.
  changeFree(src, other, value, invalid) {
    if (invalid) {
      src.classList.add('is-invalid')
      this.markWinners(null, null)
      return
    }

    src.classList.remove('is-invalid')

    if (value === null) {
      if (other.value.trim() === '') {
        this.markWinners(null, null)
        this.push(null, null)
      }
      return
    }

    if (other.value.trim() === '') {
      this.markWinners(null, null)
      return
    }

    this.pushBothScores()
  }

  pushBothScores() {
    const scoreA = parseInt(this.aTarget.value, 10)
    const scoreB = parseInt(this.bTarget.value, 10)
    this.markWinners(scoreA, scoreB)
    this.push(scoreA, scoreB)
  }

  markWinners(scoreA, scoreB) {
    const filled = scoreA !== null && scoreB !== null
    this.aTarget.classList.toggle('is-winner', filled && scoreA > scoreB)
    this.bTarget.classList.toggle('is-winner', filled && scoreB > scoreA)
  }

  push(scoreA, scoreB) {
    clearTimeout(this.timer)
    // Номер запроса: ответы приходят вне порядка, и старый ответ мог
    // перерисовать таблицу поверх более свежего.
    this.lastRequest = (this.lastRequest || 0) + 1
    const requestId = this.lastRequest
    // Открытая карточка участника живёт в ?player= — сервер обновит её тем же
    // ответом, иначе её счёт и очки остались бы старыми.
    const player = new URLSearchParams(window.location.search).get('player')

    this.timer = setTimeout(async () => {
      const body = { ['score_a']: scoreA, ['score_b']: scoreB }
      if (player !== null) body.player = player

      let response
      try {
        // Ключи в snake_case — так их ждёт Rails; bracket-нотация обходит camelcase-правило.
        response = await fetchTurbo(this.urlValue, {
          method: 'PATCH',
          body: JSON.stringify(body)
        })
      } catch {
        this.markFailed(requestId)
        return
      }
      if (requestId !== this.lastRequest) return

      if (!response.ok) {
        this.markFailed(requestId)
        return
      }

      let html
      try {
        html = await response.text()
      } catch {
        this.markFailed(requestId)
        return
      }
      // Чтение тела — тоже await: за это время пользователь мог ввести ещё
      // цифру, и её ответ свежее. Иначе устаревший HTML перерисует таблицу.
      if (requestId !== this.lastRequest) return

      window.Turbo?.renderStreamMessage(html)
    }, 500)
  }

  // Счёт не сохранился — раньше это проглатывалось молча и пользователь
  // считал, что результат записан.
  markFailed(requestId) {
    if (requestId !== this.lastRequest) return

    this.aTarget.classList.add('is-invalid')
    this.bTarget.classList.add('is-invalid')
  }

  disconnect() {
    clearTimeout(this.timer)
  }
}
